interface LogoProps {
  size?: number;
}

/// Brand mark — a circular compass rose in admiralty blue, with a
/// gold 4-point star and a scattering of pixie sparkles in the
/// surrounding ring. The compass body reads at small sizes; the
/// sparkles only land at 28 px+ but they're what gives the mark
/// its "Pixie" character. Pure SVG, no font dependency.
export function Logo({ size = 32 }: LogoProps) {
  return (
    <svg
      viewBox="0 0 40 40"
      width={size}
      height={size}
      role="img"
      aria-label="nav-games"
      style={{ display: "block" }}
    >
      <defs>
        <radialGradient id="logo-face" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#2b78ad" />
          <stop offset="100%" stopColor="#164a72" />
        </radialGradient>
      </defs>

      {/* Outer dotted ring — bearing tick marks every 30°. */}
      <g stroke="var(--accent-hi)" strokeWidth="0.7" strokeLinecap="round">
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i * 30 * Math.PI) / 180;
          const x1 = 20 + 19 * Math.sin(a);
          const y1 = 20 - 19 * Math.cos(a);
          const x2 = 20 + 17 * Math.sin(a);
          const y2 = 20 - 17 * Math.cos(a);
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
        })}
      </g>

      {/* Compass face */}
      <circle cx="20" cy="20" r="15" fill="url(#logo-face)" />

      {/* 4-point compass star — N-S elongated, E-W shorter, classic
          mariner's rose. Front face in gold, back face in cream so
          the star reads even on the light theme. */}
      <polygon
        points="20,5 22.2,20 20,35 17.8,20"
        fill="var(--accent-hi)"
      />
      <polygon
        points="5,20 20,17.8 35,20 20,22.2"
        fill="var(--accent-hi)"
        opacity="0.55"
      />

      {/* Centre pin — small white dot with a darker inner pip */}
      <circle cx="20" cy="20" r="2.2" fill="#ffffff" />
      <circle cx="20" cy="20" r="0.9" fill="#164a72" />

      {/* "N" marker at the top of the star, in admiralty blue on the
          gold spike for contrast. */}
      <text
        x="20"
        y="11.4"
        textAnchor="middle"
        fontSize="4"
        fontWeight="700"
        fill="#164a72"
        fontFamily="ui-sans-serif, system-ui"
      >
        N
      </text>

      {/* Pixie sparkles — three little four-point stars around the
          rose. Drawn as crossed diamonds so they read as "twinkles"
          rather than tick marks. */}
      <g fill="var(--accent-hi)">
        <Sparkle cx={6} cy={7} size={1.6} />
        <Sparkle cx={34} cy={6} size={1.2} />
        <Sparkle cx={33} cy={33} size={1.4} />
      </g>
    </svg>
  );
}

function Sparkle({
  cx,
  cy,
  size,
}: {
  cx: number;
  cy: number;
  size: number;
}) {
  return (
    <polygon
      points={`${cx},${cy - size} ${cx + size * 0.35},${cy - size * 0.35} ${cx + size},${cy} ${cx + size * 0.35},${cy + size * 0.35} ${cx},${cy + size} ${cx - size * 0.35},${cy + size * 0.35} ${cx - size},${cy} ${cx - size * 0.35},${cy - size * 0.35}`}
    />
  );
}
